import { AvailabilityRule, PersonAvailability, TimeInterval } from '@/types/personAvailability';
import { WEEKDAY_NAMES, WEEKDAY_ORDER } from '@/utils/calendar';
import { groupConsecutiveDates as groupDateRules } from '@/utils/dateAvailability';
import { convertTo12Hour, convertTo24Hour } from '@/utils/timeFormat';

import { AvailabilityGroup, DateRange, SummaryOptions, WeekdayGroup } from './types';

// Re-export types for convenience
export type { AvailabilityGroup, DateRange, SummaryOptions, WeekdayGroup };

/**
 * Formats a time interval as "9:00am-5:00pm"
 */
export const formatInterval = (interval: TimeInterval): string => {
  // Check if already in 12-hour format (contains 'am' or 'pm')
  const isAlready12Hour =
    interval.from.toLowerCase().includes('am') ||
    interval.from.toLowerCase().includes('pm') ||
    interval.to.toLowerCase().includes('am') ||
    interval.to.toLowerCase().includes('pm');

  const from = isAlready12Hour ? interval.from : convertTo12Hour(interval.from);
  const to = isAlready12Hour ? interval.to : convertTo12Hour(interval.to);

  return `${from}-${to}`;
};

/**
 * Formats multiple intervals as "9:00am-12:00pm, 1:00pm-5:00pm"
 */
export const formatIntervals = (intervals: TimeInterval[]): string => {
  const sortedIntervals = [...intervals].sort((a, b) => {
    const fromDiff = parseTimeToMinutes(a.from) - parseTimeToMinutes(b.from);
    if (fromDiff !== 0) return fromDiff;
    return parseTimeToMinutes(a.to) - parseTimeToMinutes(b.to);
  });

  return sortedIntervals.map(formatInterval).join(', ');
};

const parseTimeToMinutes = (time: string): number => {
  try {
    const normalized = convertTo24Hour(time);
    const [hours, minutes] = normalized.split(':').map(Number);
    return hours * 60 + minutes;
  } catch {
    return Number.POSITIVE_INFINITY;
  }
};

/**
 * Formats a date as "Dec 25" or "Jan 1" (without year for cleaner display in summary)
 */
const formatDate = (dateStr: string): string => {
  const date = new Date(dateStr + 'T00:00:00'); // Parse as local date
  const month = date.toLocaleDateString('en-US', { month: 'short' });
  const day = date.getDate();
  return `${month} ${day}`;
};

/**
 * Formats a date range for display in the summary.
 */
const formatDateRange = (startStr: string, endStr: string): string => {
  if (startStr === endStr) {
    return formatDate(startStr);
  }

  const startDate = new Date(startStr + 'T00:00:00');
  const endDate = new Date(endStr + 'T00:00:00');

  const startMonth = startDate.toLocaleDateString('en-US', { month: 'short' });
  const startDay = startDate.getDate();
  const endMonth = endDate.toLocaleDateString('en-US', { month: 'short' });
  const endDay = endDate.getDate();
  const startYear = startDate.getFullYear();
  const endYear = endDate.getFullYear();

  // If the range spans different years, include the year for both dates:
  // "Dec 30, 2025 - Jan 2, 2026"
  if (startYear !== endYear) {
    return `${startMonth} ${startDay}, ${startYear} - ${endMonth} ${endDay}, ${endYear}`;
  }

  // Same year: use compact formats
  // If different months, show both: "Dec 25 - Jan 5"
  if (startMonth !== endMonth) {
    return `${startMonth} ${startDay} - ${endMonth} ${endDay}`;
  }

  // Same month: "Feb 9-20"
  return `${startMonth} ${startDay}-${endDay}`;
};

/**
 * Groups consecutive unavailable dates into ranges.
 * Uses the existing groupConsecutiveDates from dateAvailability.ts
 * and converts to the DateRange format needed for the summary.
 */
const groupConsecutiveDates = (dates: string[]): DateRange[] => {
  if (dates.length === 0) return [];

  // Create simple rules for grouping - all with empty intervals (unavailable)
  const rules: AvailabilityRule[] = dates.map((date) => ({
    type: 'date' as const,
    date,
    intervals: []
  }));

  const groups = groupDateRules(rules);

  return groups.map((group) => ({
    start: group.startDate,
    end: group.endDate,
    count: group.dates.length
  }));
};

/**
 * Groups weekdays with the same intervals
 * Returns an array of groups like [{ days: ['monday', 'tuesday'], intervals: [...] }]
 */
export const groupWeekdaysByIntervals = (rules: AvailabilityRule[]): WeekdayGroup[] => {
  // Filter to only weekday rules
  const weekdayRules = rules.filter((rule) => rule.type === 'weekday' && rule.weekday);

  if (weekdayRules.length === 0) return [];

  const groups: WeekdayGroup[] = [];

  // Group by same intervals
  // Map key is deterministic interval string, value contains the intervals and weekdays
  const intervalMap = new Map<string, { intervals: TimeInterval[]; weekdays: string[] }>();

  for (const rule of weekdayRules) {
    // Sort intervals deterministically to ensure consistent grouping
    const sortedIntervals = [...rule.intervals].sort((a, b) => {
      if (a.from !== b.from) return a.from.localeCompare(b.from);
      return a.to.localeCompare(b.to);
    });

    // Create a deterministic, human-readable key from sorted intervals
    const intervalKey = sortedIntervals.map((int) => `${int.from}-${int.to}`).join(',');

    if (!intervalMap.has(intervalKey)) {
      intervalMap.set(intervalKey, { intervals: sortedIntervals, weekdays: [] });
    }
    intervalMap.get(intervalKey)!.weekdays.push(rule.weekday!);
  }

  // Convert map to groups and sort days
  for (const { intervals, weekdays } of intervalMap.values()) {
    // Sort days by weekday order
    const sortedDays = weekdays.sort((a, b) => {
      const aIndex = WEEKDAY_ORDER.indexOf(a);
      const bIndex = WEEKDAY_ORDER.indexOf(b);
      return aIndex - bIndex;
    });

    groups.push({
      days: sortedDays,
      intervals
    });
  }

  // Sort groups by the first day in each group to maintain week order
  groups.sort((a, b) => {
    const aFirstDayIndex = WEEKDAY_ORDER.indexOf(a.days[0]);
    const bFirstDayIndex = WEEKDAY_ORDER.indexOf(b.days[0]);
    return aFirstDayIndex - bFirstDayIndex;
  });

  return groups;
};

/**
 * Formats a group of weekdays as "Mon-Fri" or "Mon, Wed, Fri"
 */
export const formatWeekdayLabel = (days: string[]): string => {
  if (days.length === 1) {
    return WEEKDAY_NAMES[days[0]].substring(0, 3); // "Mon"
  }

  // Check if truly consecutive in WEEKDAY_ORDER
  const indices = days.map((day) => WEEKDAY_ORDER.indexOf(day));
  const isConsecutive = indices.every((idx, i) => i === 0 || idx === indices[i - 1] + 1);

  if (isConsecutive) {
    const firstDay = WEEKDAY_NAMES[days[0]].substring(0, 3);
    const lastDay = WEEKDAY_NAMES[days[days.length - 1]].substring(0, 3);
    return `${firstDay}-${lastDay}`;
  } else {
    // Non-consecutive days: "Mon, Wed, Fri"
    return days.map((day) => WEEKDAY_NAMES[day].substring(0, 3)).join(', ');
  }
};

/**
 * Formats a group of weekdays as "Available Mon-Fri 9:00am-5:00pm" (for string output)
 */
export const formatWeekdayGroup = (group: WeekdayGroup): string => {
  const { days, intervals } = group;

  const dayStr = formatWeekdayLabel(days);

  // Handle unavailable (no intervals)
  if (intervals.length === 0) {
    return `Unavailable ${dayStr}`;
  }

  // Format intervals
  const intervalStr = formatIntervals(intervals);

  return `Available ${dayStr} ${intervalStr}`;
};

/**
 * Generates a human-readable summary string from PersonAvailability rules
 * (Used for backward compatibility with tests)
 *
 * Examples:
 * - "Available Mon-Fri 9:00am-5:00pm"
 * - "Available Mon, Wed, Fri 10:00am-2:00pm, 3:00pm-6:00pm"
 * - "Unavailable Dec 25, Jan 1"
 * - "No regular schedule set"
 */
export function generateAvailabilitySummary(
  availability: PersonAvailability | null,
  options?: SummaryOptions
): string {
  const maxDateExceptions = options?.maxDateExceptions ?? 3;

  // Handle empty availability
  if (!availability || !availability.rules || availability.rules.length === 0) {
    return 'No regular schedule set';
  }

  const { rules } = availability;

  // Separate weekday rules and date-specific rules
  const weekdayRules = rules.filter((rule) => rule.type === 'weekday');
  const dateRules = rules
    .filter((rule) => rule.type === 'date' && rule.date)
    .sort((a, b) => {
      // Sort by date
      return a.date! < b.date! ? -1 : 1;
    });

  const summaryParts: string[] = [];

  // Process weekday rules
  if (weekdayRules.length > 0) {
    const groups = groupWeekdaysByIntervals(weekdayRules);
    groups.forEach((group) => {
      summaryParts.push(formatWeekdayGroup(group));
    });
  }

  // Process date-specific exceptions (limited by maxDateExceptions)
  if (dateRules.length > 0) {
    // Separate available and unavailable dates
    const availableDateRules = dateRules.filter((rule) => rule.intervals.length > 0);
    const unavailableDateRules = dateRules.filter((rule) => rule.intervals.length === 0);

    // Group unavailable dates into ranges
    if (unavailableDateRules.length > 0) {
      const unavailableDates = unavailableDateRules.map((rule) => rule.date!);
      const dateRanges = groupConsecutiveDates(unavailableDates);

      const rangeLabels = dateRanges.map((range) => {
        const label =
          range.start === range.end
            ? formatDate(range.start)
            : formatDateRange(range.start, range.end);
        return range.count > 1 ? `${label} (${range.count} days)` : label;
      });

      summaryParts.push(`Unavailable ${rangeLabels.join(', ')}`);
    }

    // For available date exceptions, keep individual entries
    const availableToShow = availableDateRules.slice(0, maxDateExceptions);
    availableToShow.forEach((rule: AvailabilityRule) => {
      const dateStr = formatDate(rule.date!);
      const intervalStr = formatIntervals(rule.intervals);
      summaryParts.push(`Available ${dateStr} ${intervalStr}`);
    });

    // Add indicator if there are more available date exceptions
    if (availableDateRules.length > maxDateExceptions) {
      const remaining = availableDateRules.length - maxDateExceptions;
      summaryParts.push(`+${remaining} more date${remaining === 1 ? '' : 's'}`);
    }
  }

  // If no summary parts generated, return default
  if (summaryParts.length === 0) {
    return 'No regular schedule set';
  }

  return summaryParts.join('. ');
}

/**
 * Generates structured availability data for rendering
 */
export function generateAvailabilityGroups(
  availability: PersonAvailability | null,
  options?: SummaryOptions
): AvailabilityGroup[] {
  const maxDateExceptions = options?.maxDateExceptions ?? 3;

  // Handle empty availability
  if (!availability || !availability.rules || availability.rules.length === 0) {
    return [];
  }

  const { rules } = availability;

  // Separate weekday rules and date-specific rules
  const weekdayRules = rules.filter((rule) => rule.type === 'weekday');
  const dateRules = rules
    .filter((rule) => rule.type === 'date' && rule.date)
    .sort((a, b) => {
      // Sort by date
      return a.date! < b.date! ? -1 : 1;
    });

  const groups: AvailabilityGroup[] = [];

  // Process weekday rules
  if (weekdayRules.length > 0) {
    const weekdayGroups = groupWeekdaysByIntervals(weekdayRules);
    weekdayGroups.forEach((group) => {
      const dayLabel = formatWeekdayLabel(group.days);

      if (group.intervals.length === 0) {
        // Unavailable
        groups.push({
          type: 'unavailable' as const,
          label: dayLabel
        });
      } else {
        // Available with intervals
        groups.push({
          type: 'available' as const,
          label: dayLabel,
          intervals: formatIntervals(group.intervals)
        });
      }
    });
  }

  // Process date-specific exceptions
  if (dateRules.length > 0) {
    // Separate available and unavailable dates
    const availableDateRules = dateRules.filter((rule) => rule.intervals.length > 0);
    const unavailableDateRules = dateRules.filter((rule) => rule.intervals.length === 0);

    // Group unavailable dates into ranges
    if (unavailableDateRules.length > 0) {
      const unavailableDates = unavailableDateRules.map((rule) => rule.date!);
      const dateRanges = groupConsecutiveDates(unavailableDates);

      dateRanges.forEach((range) => {
        const label = formatDateRange(range.start, range.end);
        groups.push({
          type: 'unavailable' as const,
          label,
          dateRange: range.count > 1,
          dayCount: range.count
        });
      });
    }

    // For available date exceptions, keep individual entries (since they have custom intervals)
    availableDateRules.slice(0, maxDateExceptions).forEach((rule: AvailabilityRule) => {
      const dateStr = formatDate(rule.date!);
      groups.push({
        type: 'available' as const,
        label: dateStr,
        intervals: formatIntervals(rule.intervals)
      });
    });

    // Add indicator if there are more available date exceptions
    if (availableDateRules.length > maxDateExceptions) {
      const remaining = availableDateRules.length - maxDateExceptions;
      groups.push({
        type: 'unavailable' as const,
        label: `+${remaining} more date${remaining === 1 ? '' : 's'}`
      });
    }
  }

  return groups;
}
